import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../api/client';
import { useAsync } from '../hooks/useAsync';
import { useSideNav } from '../context/SideNavContext';
import PageHeader from '../components/PageHeader';
import Card from '../components/Card';
import ProviderAvatar from '../components/ProviderAvatar';
import StatusBadge from '../components/StatusBadge';
import StatCard from '../components/StatCard';
import { Loading, ErrorState, EmptyState } from '../components/States';
import { currency, relativeAge } from '../lib/format';
import { providerNav } from '../lib/providerNav';

export default function ProviderDetail() {
  const { id } = useParams();
  const { setNav } = useSideNav();
  const { data, loading, error, reload } = useAsync(
    () =>
      Promise.all([
        api.getProvider(id),
        // The envelope, for meta.syncedAt: these regions are a stored snapshot of
        // the tenancy's subscriptions, and the card says how old it is rather than
        // presenting a month-old list as current. Refreshing it lives on the
        // Region subscriptions page.
        api.getRegionsEnvelope(id),
        api.getServices(id),
        api.getCosts(id),
        api.getResources({ providerId: id }),
      ]).then(([provider, regions, services, costs, resources]) => ({
        provider,
        regions: regions.data || [],
        regionsSyncedAt: regions.meta?.syncedAt || null,
        services,
        costs,
        resources,
      })),
    [id]
  );

  // Publish the provider submenu (Overview · Public price list) while mounted.
  const provider = data?.provider;
  useEffect(() => {
    if (provider) setNav(providerNav(provider));
  }, [provider, setNav]);
  useEffect(() => () => setNav(null), [setNav]);

  if (loading) return <Loading label="Loading provider…" />;
  if (error) return <ErrorState error={error} onRetry={reload} />;

  const { regions, regionsSyncedAt, services, costs, resources } = data;
  const homeRegion = regions.find((r) => r.home);
  const running = resources.filter((r) => r.status === 'running').length;

  // Group services by category for the catalog.
  const byCategory = services.reduce((acc, s) => {
    (acc[s.category] ||= []).push(s);
    return acc;
  }, {});

  return (
    <div className="stack">
      <PageHeader
        title={
          <span className="inline-title">
            <ProviderAvatar provider={provider} size={36} logo />
            {provider.name}
          </span>
        }
        subtitle={provider.description}
        actions={
          <a className="btn btn--ghost" href={provider.website} target="_blank" rel="noreferrer">
            Open console ↗
          </a>
        }
      />

      <div className="breadcrumb">
        <Link to="/providers" className="link">Providers</Link>
        <span>/</span>
        <span>{provider.shortName}</span>
      </div>

      <div className="grid grid--kpi">
        <StatCard label="Status" value={<StatusBadge status={provider.status} />} />
        <StatCard label="Monthly spend" value={currency(costs.monthlyCost, costs.currency)} trend={costs.changePercent} />
        <StatCard label="Resources" value={resources.length} hint={`${running} running`} />
        <StatCard
          label="Regions"
          value={regions.length}
          hint={
            regions.length === 0
              ? 'not synced yet'
              : homeRegion
              ? `subscribed · home ${homeRegion.id}`
              : 'subscribed'
          }
        />
      </div>

      <div className="grid grid--2">
        <Card title="Service catalog">
          {Object.entries(byCategory).map(([cat, svcs]) => (
            <div key={cat} className="catalog-group">
              <div className="catalog-group__title">{cat}</div>
              <div className="chip-row">
                {svcs.map((s) => (
                  <span key={s.id} className="chip" title={s.description}>
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </Card>

        <Card
          title="Subscribed regions"
          action={
            <Link className="btn btn--ghost btn--sm" to={`/providers/${id}/regions`}>
              Manage
            </Link>
          }
        >
          {regions.length === 0 ? (
            <EmptyState
              title="Not synced yet"
              detail="Refresh the region subscriptions to read them from the provider."
            />
          ) : (
            <table className="table">
              <thead>
                <tr>
                  <th>Region</th>
                  <th>Key</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {regions.map((r) => (
                  <tr key={r.id}>
                    <td>
                      <code className="mono">{r.id}</code>
                      {r.home ? (
                        <span className="badge badge--info" style={{ marginLeft: 8 }}>
                          Home
                        </span>
                      ) : null}
                    </td>
                    <td className="cell-type">{r.key || '—'}</td>
                    <td><StatusBadge status={r.status} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="field__hint">
            The regions this tenancy is signed up to — a resource can only exist in
            one of them, and the home region holds IAM and cannot be unsubscribed.
            {relativeAge(regionsSyncedAt)
              ? ` Stored snapshot, synced ${relativeAge(regionsSyncedAt)}.`
              : ''}
          </p>
        </Card>
      </div>

      <Card
        title="Resources"
      >
        <table className="table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Category</th>
              <th>Region</th>
              <th>Status</th>
              <th className="num">Monthly cost</th>
            </tr>
          </thead>
          <tbody>
            {resources.map((r) => (
              <tr key={r.id}>
                <td>
                  <div className="cell-primary">{r.name}</div>
                  <div className="cell-sub mono">{r.spec}</div>
                </td>
                <td>{r.category}</td>
                <td><code className="mono">{r.region}</code></td>
                <td><StatusBadge status={r.status} /></td>
                <td className="num">{currency(r.monthlyCost)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
